import { useState } from "react";
import { Button, Header4 } from "../../../../components/ui";
import { PasswordInput } from "../../../../components/ui/inputs";
import { ValidationRule } from "../ValidationRule";
import userApi from "../../api/user.api";
import { toastService as toast } from "../../../../components/ui/toast/toast.service";
import localization from "../../../../config/localization";
import { DeviceType } from "../../../../models/constants/deviceType";
import userDeviceStore from "../../../../store/device.store";
import { useNavigate } from "react-router-dom";
import errorModal from "../../../../services/errorModal.service";
import { localStorageService } from "../../../../services";

interface ChangePasswordProps {
  onClose?: () => void;
}

export function ChangePassword({ onClose }: ChangePasswordProps) {
  const deviceType = userDeviceStore((state) => state.deviceType);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [newPasswordTouched, setNewPasswordTouched] = useState(false);
  const [currentPasswordError, setCurrentPasswordError] = useState<
    string | null
  >(null);

  const isEntered = newPassword.length > 0;

  const username = localStorageService.getItem("username") ?? "";

  const isLengthValid = newPassword.length >= 6 && newPassword.length <= 20;

  const navigate = useNavigate();

  const isCharValid =
    newPassword.length > 0 &&
    /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]+$/.test(
      newPassword,
    );

  const isDifferentFromCurrent =
    currentPassword.length > 0 &&
    newPassword.length > 0 &&
    newPassword !== currentPassword;

  const isDifferentFromUsername =
    newPassword.length > 0 && newPassword !== username;

  const isConfirmMatch =
    confirmPassword.length > 0 && confirmPassword === newPassword;

  const isValid =
    isEntered &&
    isLengthValid &&
    isCharValid &&
    isDifferentFromCurrent &&
    isDifferentFromUsername &&
    isConfirmMatch;

  const handleSubmit = () => {
    if (!isValid) return;

    setCurrentPasswordError(null);

    userApi
      .changePassword({
        currentPassword,
        password: newPassword,
        confirmPassword,
      })
      .then((response) => {
        if (response.isSuccess) {
          toast.success(localization.password_changed);
          onClose?.();
          navigate("/profile");
          return;
        }

        setCurrentPasswordError(
          localization.sql_error_provide_valid_old_password_1,
        );
      })
      .catch(() => {
        errorModal.showErrorModal(undefined, "Something went wrong");
      });
  };

  return (
    <div className="p-4">
      <div>
        <Header4 className="text-white mb-5 font-semibold">
          {localization.change_password}
        </Header4>
      </div>
      <div
        className={`mt-4 flex gap-4 ${deviceType === DeviceType.Web ? "flex-row items-start" : "flex-col"
          }`}
      >
        <PasswordInput
          label={localization.password}
          placeholder={localization.enter_current_password}
          maxLength={20}
          value={currentPassword}
          error={currentPasswordError ?? undefined}
          onChange={(e) => {
            setCurrentPassword(e.target.value);
            setCurrentPasswordError(null);
          }}
        />

        <PasswordInput
          label={localization.new_password}
          placeholder={localization.enter_new_password}
          maxLength={20}
          value={newPassword}
          onChange={(e) => {
            setNewPassword(e.target.value);
            setNewPasswordTouched(true);
          }}
        />

        <PasswordInput
          label={localization.confirm_password}
          placeholder={localization.enter_confirm_password}
          maxLength={20}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
        />
      </div>

      <div className="mt-4 space-y-1">
        <ValidationRule
          valid={isEntered}
          touched={newPasswordTouched}
          text={localization.change_password_validation_required}
        />

        <ValidationRule
          valid={isLengthValid}
          touched={newPasswordTouched}
          text={localization.change_password_validation_length}
        />

        <ValidationRule
          valid={isCharValid}
          touched={newPasswordTouched}
          text={localization.change_password_validation_charset}
        />

        <ValidationRule
          valid={isDifferentFromCurrent}
          touched={newPasswordTouched}
          text={localization.change_password_validation_not_same_as_current}
        />

        <ValidationRule
          valid={isDifferentFromUsername}
          touched={newPasswordTouched}
          text={localization.change_password_validation_not_same_as_username}
        />
      </div>

      <div
        className={`mt-4 ${deviceType === DeviceType.Web ? "flex justify-center" : ""}`}
      >
        <Button
          type="submit"
          disabled={!isValid}
          onClick={handleSubmit}
          className={`mt-2 ${deviceType === DeviceType.Web ? "w-1/3" : "w-full"} py-4 rounded-lg text-white bg-primary-gradient uppercase tracking-wider
          ${!isValid ? "opacity-50 cursor-not-allowed" : "hover:bg-primary-gradient/80 cursor-pointer"}`}
        >
          {localization.submit}
        </Button>
      </div>
    </div>
  );
}
